import Lottie from 'lottie-react';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { IconBuildingWarehouse, IconBook, IconUsers, IconArrowRight } from '@tabler/icons-react';

import { UserContext } from 'contexts';
import { Card, CardContent } from '@/components/ui/card';
import rocketLaunch from './rocket-lunch.json';

const CLAUDE_ICON = 'https://upload.wikimedia.org/wikipedia/commons/b/b0/Claude_AI_symbol.svg';

const SECTIONS = [
    {
        title: 'Entrepôt',
        subtitle: 'WMS',
        description: 'Stocks, livraisons, achats fournisseurs et logistique',
        icon: IconBuildingWarehouse,
        color: 'from-emerald-500/10 to-emerald-500/5',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        borderColor: 'hover:ring-emerald-500/30',
        links: [
            { label: 'Livraisons', url: '/deliveries' },
            { label: 'Achats', url: '/supply-orders' },
            { label: 'Transferts', url: '/stock-transfers' },
            { label: 'MS Groupes', url: '/microstore-column-groups' },
        ],
    },
    {
        title: 'Catalogue',
        subtitle: 'CMS',
        description: 'Produits, catégories, fournisseurs et gammes',
        icon: IconBook,
        color: 'from-blue-500/10 to-blue-500/5',
        iconColor: 'text-blue-600 dark:text-blue-400',
        borderColor: 'hover:ring-blue-500/30',
        links: [
            { label: 'Produits', url: '/products' },
            { label: 'Gammes', url: '/product-arrangements' },
            { label: 'Menus', url: '/menus' },
            { label: 'Templates', url: '/microstore-column-templates' },
        ],
    },
    {
        title: 'Clients',
        subtitle: 'Customer',
        description: 'Commandes, organisations, paiements et engagement',
        icon: IconUsers,
        color: 'from-violet-500/10 to-violet-500/5',
        iconColor: 'text-violet-600 dark:text-violet-400',
        borderColor: 'hover:ring-violet-500/30',
        links: [
            { label: 'Organisations', url: '/organizations' },
            { label: 'Sites', url: '/sites' },
            { label: 'Utilisateurs', url: '/users' },
            { label: 'Transactions', url: '/transactions' },
        ],
    },
];

export const Home = () => {
    const user = useContext(UserContext);
    const firstname = user?.profile?.firstname;

    const now = new Date();
    const hour = now.getHours();
    const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

    return (
        <div className="flex flex-1 flex-col gap-8 overflow-y-auto p-6 md:p-10">
            <Helmet title="Accueil · TOTADMIN" />

            {/* Hero */}
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
                    {greeting}
                    {firstname ? `, ${firstname}` : ''} 👋
                </h1>
                <p className="text-muted-foreground text-lg">Bienvenue sur le panel d'administration Totem</p>
            </div>

            {/* System Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                {SECTIONS.map((section) => {
                    const Icon = section.icon;
                    return (
                        <Card
                            key={section.title}
                            className={`relative overflow-hidden bg-linear-to-br ${section.color} transition-all duration-200 ${section.borderColor}`}
                        >
                            <CardContent className="flex flex-col gap-4 pt-2">
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`flex size-10 items-center justify-center rounded-lg bg-background ring-1 ring-foreground/10 ${section.iconColor}`}
                                    >
                                        <Icon size={22} />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold leading-tight">{section.title}</h2>
                                        <span className="text-xs text-muted-foreground">{section.subtitle}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground">{section.description}</p>
                                <div className="flex flex-wrap gap-2">
                                    {section.links.map((link) => (
                                        <Link
                                            key={link.url}
                                            to={link.url}
                                            className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2.5 py-1 text-xs font-medium ring-1 ring-foreground/10 transition-colors hover:bg-background hover:ring-foreground/20"
                                        >
                                            {link.label}
                                            <IconArrowRight size={12} className="opacity-50" />
                                        </Link>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Claude shortcut */}
            <a
                href="claude://"
                className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg bg-background px-3 py-2 shadow-md ring-1 ring-foreground/10 transition-colors hover:bg-muted"
            >
                <img src={CLAUDE_ICON} alt="Claude" className="size-8" />
                <span className="text-sm font-medium text-muted-foreground">Ouvrir Claude</span>
            </a>

            {/* Rocket Animation */}
            <div className="flex items-center justify-center">
                <Lottie animationData={rocketLaunch} loop style={{ width: 350, height: 350 }} />
            </div>

            {/* Footer hint */}
            <div className="mt-auto flex items-center justify-center pb-4">
                <p className="text-xs text-muted-foreground">
                    Utilisez la sidebar ou{' '}
                    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono">⌘K</kbd>{' '}
                    pour naviguer rapidement
                </p>
            </div>
        </div>
    );
};
